import { externalOpponentLabel, formatTime12, usDate } from '@region13/shared-types'
import type { CoachGame } from './api'

/** Today as an ISO `YYYY-MM-DD` string (local date), for comparing against the
 *  ISO game dates. String compare is valid for zero-padded ISO dates. */
function todayIso(): string {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** True for a game dated today or earlier: one a report can be filed about. */
export function hasStarted(date: string | null): boolean {
  return (date ?? '') !== '' && (date ?? '') <= todayIso()
}

/** True when an ISO date is today-or-later (upcoming). */
export function isUpcoming(date: string | null): boolean {
  return (date ?? '') >= todayIso()
}

/** The team's next game: the first game dated today-or-later. Games arrive
 *  ordered by date/time from the API. Returns null when none remain. */
export function nextGame(games: CoachGame[]): CoachGame | null {
  return games.find((g) => isUpcoming(g.date)) ?? null
}

/** The opponent designation on a game, relative to `teamExt`. For an Extra
 *  team's own home fixture (design spec §7) `team2` is null and
 *  `external_opponent` carries the bare label instead — rendered with the
 *  "· Extra match" marker so it reads the same everywhere that marker shows. */
export function opponentOf(
  game: Pick<CoachGame, 'team1' | 'team2' | 'external_opponent'>,
  teamExt: string,
): string {
  if (game.external_opponent) return externalOpponentLabel(game.external_opponent)
  return (game.team1 === teamExt ? game.team2 : game.team1) ?? '—'
}

/** One-line game summary, e.g. "Game 7263: 10/10/2026, 1:30 PM (Victory Park 3)
 *  vs. B10-22", read like the copy line (owner, 2026-10-05). The "Game N:"
 *  prefix drops when there's no InLeague game number; the field parens drop
 *  when unknown. The opponent is formatted here, so a stored id from any caller
 *  (the Referees screen's server-sent opponent included) prints as `B10-22`; a
 *  fixture label passes through unchanged. Shared by Home, Game cards, and
 *  Referees so the format can't drift. */
export function gameLine(
  game: { date: string | null; time: string; field_name: string | null; gameNum?: string | null },
  opponent: string,
): string {
  const prefix = game.gameNum ? `Game ${game.gameNum}: ` : ''
  const date = game.date ? usDate(game.date) : 'Date TBD'
  const where = game.field_name ? ` (${game.field_name})` : ''
  return `${prefix}${date}, ${formatTime12(game.time)}${where} vs. ${opponent}`
}
